/**
 * หน้าหลัก — "ศูนย์งานวันนี้" (Today Command Center).
 *
 * แทนที่ grid ปุ่มลอย ๆ ด้วยลำดับ: แถบสถานะ → setup checklist (แอดมิน) →
 * งานด่วนวันนี้ (briefing) → action ด่วนวันนี้ (เช็คอิน/ลา/OT/สลิป) →
 * เมนูที่เหลือแบ่งหมวดตาม role. เลือกแสดงเฉพาะสิ่งที่ role นั้นมีสิทธิ์ทำ.
 */
import { Link } from 'react-router-dom';
import { menuAllowedForRole } from '../lib/menu';
import { useAuth } from '../hooks/useAuth';
import { useCheckinStatus, useMyTenants, useSwitchTenant, useMyOnboarding } from '../hooks';
import { useAuthStore } from '../lib/store';
import { useToast } from '../components/ui/toast';
import { BottomTabBar } from '../components/layout/BottomTabBar';
import { DailyBriefing } from '../components/DailyBriefing';
import { SetupCard } from '../components/SetupCard';
import { BigActionButton } from '../components/ui/BigActionButton';
import type { CheckinKind } from '@payroll/shared';
import {
  Camera, FileText, Clock, Wallet, BarChart3, Users, Settings, ListChecks,
  UserCircle, MessageSquareWarning, LogIn, Sparkles, Search, ChevronRight, Calendar,
  Building2, Plus, ArrowRightLeft, ScrollText, BookOpen, CalendarRange, CalendarClock, Repeat,
  FolderArchive, ClipboardCheck, Landmark,
} from 'lucide-react';

type NavRole = 'OWNER' | 'HR' | 'SUPERVISOR' | 'EMPLOYEE';

interface MenuItem {
  to: string;
  label: string;
  icon: typeof Camera;
  roles?: NavRole[];
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

/** action ด่วนวันนี้ — ปุ่มใหญ่ tap เดียวถึง (item 5: Employee Home) */
const FAST_ACTIONS: MenuItem[] = [
  { to: '/checkin', label: 'เช็คอิน', icon: Camera },
  { to: '/leave', label: 'ขอลา', icon: FileText },
  { to: '/ot', label: 'ขอ OT', icon: Clock },
  { to: '/slip', label: 'สลิปเงินเดือน', icon: Wallet },
];

/** เมนูที่เหลือ แบ่งหมวดตาม workflow (ไม่ซ้ำกับ fast actions) */
const GROUPS: MenuGroup[] = [
  {
    title: 'เวลาและคำขอ',
    items: [
      { to: '/my-schedule', label: 'กะทำงานของฉัน', icon: CalendarClock },
      { to: '/swap', label: 'สลับเวร', icon: ArrowRightLeft },
      { to: '/shift-change', label: 'ขอย้ายวันหยุด', icon: CalendarRange },
      { to: '/change-shift', label: 'ขอเปลี่ยนกะการทำงาน', icon: Repeat },
      { to: '/confirm-clockout', label: 'ยืนยันเวลาเลิกงาน', icon: LogIn },
      { to: '/attendance-correction', label: 'รับรองเวลาเข้างาน', icon: Clock },
      { to: '/quota', label: 'สิทธิ์การลา', icon: BarChart3 },
      { to: '/me/attendance', label: 'ประวัติการเข้างาน', icon: Calendar },
      { to: '/policies', label: 'นโยบายการทำงาน', icon: BookOpen },
    ],
  },
  {
    title: 'ของฉัน',
    items: [
      { to: '/profile', label: 'ประวัติของฉัน', icon: UserCircle },
      { to: '/my-documents', label: 'เอกสารของฉัน', icon: FolderArchive },
      { to: '/company-docs', label: 'เอกสารบริษัท', icon: Landmark },
      { to: '/tickets', label: 'ร้องเรียน / แจ้งเหตุ', icon: MessageSquareWarning },
      { to: '/factsheet', label: 'หลักการคำนวณ', icon: ScrollText },
    ],
  },
  {
    title: 'ผู้ช่วย',
    items: [
      { to: '/ask', label: 'ผู้ช่วย AI', icon: Sparkles },
      { to: '/find', label: 'ค้นหาเมนู / พาไป', icon: Search },
    ],
  },
  {
    title: 'งานแอดมิน',
    items: [
      { to: '/approvals', label: 'อนุมัติคำขอ', icon: ListChecks, roles: ['OWNER', 'HR', 'SUPERVISOR'] },
      { to: '/team/schedule', label: 'จัดกะทีมงาน', icon: CalendarRange, roles: ['SUPERVISOR'] },
      { to: '/hr', label: 'แดชบอร์ด HR', icon: Users, roles: ['OWNER', 'HR'] },
      { to: '/owner', label: 'ตั้งค่าระบบ', icon: Settings, roles: ['OWNER'] },
    ],
  },
];

/** ลำดับการเช็คอินใน 1 วัน + label ของรอบถัดไป (ตรงกับ Checkin.tsx) */
const CHECKIN_ORDER: CheckinKind[] = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'];
const CHECKIN_LABEL: Record<CheckinKind, string> = {
  morning_in: 'เข้างาน',
  lunch_out: 'ออกพัก',
  lunch_in: 'กลับจากพัก',
  evening_out: 'เลิกงาน',
};

function FastActionTile({ item, badge }: { item: MenuItem; badge?: string }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className="card relative flex flex-col items-center justify-center gap-2 py-5 hover:bg-gray-50 transition"
    >
      <Icon className="w-8 h-8 text-brand-600" />
      <span className="text-sm font-semibold">{item.label}</span>
      {badge && (
        <span className="absolute top-2 right-2 text-[10px] bg-brand-50 text-brand-700 rounded-full px-2 py-0.5 font-medium">
          {badge}
        </span>
      )}
    </Link>
  );
}

/**
 * HomeTenantStrip — multi-tenant affordance inside the Home gradient header.
 * Renders nothing when the user belongs to a single tenant (no UI noise);
 * a 1-line strip with "+ ผูกบริษัทเพิ่ม" link appears when they could pair
 * more, and a horizontal pill row with the other tenants when they already
 * have multiple memberships. Tap a pill → switch session to that tenant.
 */
function HomeTenantStrip() {
  const profile = useAuthStore((s) => s.profile);
  const { data: memberships } = useMyTenants();
  const switchT = useSwitchTenant();
  const toast = useToast();
  const others = (memberships ?? []).filter((m) => m.tenant_id !== profile?.tenant_id);

  if (others.length === 0) {
    // Single tenant — show the "add another" affordance only (subtle).
    return (
      <Link
        to="/pair?add=1"
        className="mt-3 inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-xs font-medium"
      >
        <Plus className="w-3.5 h-3.5" /> เพิ่มบริษัท (ทำงานหลายบริษัท)
      </Link>
    );
  }

  async function handleSwitch(tenantId: string) {
    try {
      await switchT.mutateAsync(tenantId);
      toast.success('สลับบริษัทแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สลับบริษัทไม่สำเร็จ');
    }
  }

  return (
    <div className="mt-3 -mx-1 flex items-center gap-2 overflow-x-auto pb-1">
      <span className="inline-flex items-center gap-1 text-xs opacity-80 shrink-0 px-1">
        <ArrowRightLeft className="w-3 h-3" /> สลับ:
      </span>
      {others.map((m) => (
        <button
          key={m.tenant_id}
          onClick={() => handleSwitch(m.tenant_id)}
          disabled={switchT.isPending}
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1 text-xs shrink-0 disabled:opacity-60"
        >
          <Building2 className="w-3 h-3" />
          <span className="font-medium truncate max-w-[120px]">{m.tenant_name}</span>
        </button>
      ))}
      <Link
        to="/pair?add=1"
        className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1 text-xs shrink-0"
      >
        <Plus className="w-3 h-3" /> เพิ่ม
      </Link>
    </div>
  );
}

export function Home() {
  const { profile } = useAuth();
  const { data: checkin } = useCheckinStatus();
  const { data: myOnboarding } = useMyOnboarding();

  if (!profile) return null;

  // MANAGER ใช้สิทธิ์เมนูเท่ากับ HR
  const navRole: NavRole = profile.role === 'MANAGER' ? 'HR' : (profile.role as NavRole);
  const isAdmin = navRole === 'OWNER' || navRole === 'HR';

  const visibleGroups = GROUPS
    // 🚨 กรองด้วย role จริงด้วย — navRole map MANAGER→HR จึงพ่วงเมนูเงินมาทั้งชุด
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) => (!it.roles || it.roles.includes(navRole)) && menuAllowedForRole(it.to, profile.role),
      ),
    }))
    .filter((g) => g.items.length > 0);

  // "สิ่งที่ต้องทำ (พนักงานใหม่)" — เฉพาะตอนกำลัง onboard อยู่จริง (ไม่ noise หลัง onboard เสร็จ)
  if (myOnboarding?.status === 'active') {
    const mine = visibleGroups.find((g) => g.title === 'ของฉัน');
    if (mine) {
      mine.items = [{ to: '/my-onboarding', label: 'สิ่งที่ต้องทำ (พนักงานใหม่)', icon: ClipboardCheck }, ...mine.items];
    }
  }

  // สถานะเช็คอินวันนี้ → แสดงในแถบหัว + badge ปุ่มเช็คอิน
  // รอบที่ต้องสแกนมาจาก setting ของบริษัท (2 หรือ 4 รอบ) — ดู `kinds` จาก API
  const checkinRounds = checkin?.kinds?.length ? checkin.kinds : CHECKIN_ORDER;
  const nextKind = checkin ? checkinRounds.find((k) => !checkin.events?.[k]) ?? null : null;
  const checkinBadge = checkin ? (nextKind ? CHECKIN_LABEL[nextKind] : 'ครบแล้ว') : undefined;
  const shift = checkin?.shift ?? null;

  return (
    <div className="min-h-screen pb-20">
      {/* แถบสถานะหัวหน้า */}
      <header className="bg-brand-gradient text-white px-4 py-6">
        <p className="text-sm opacity-90">สวัสดี</p>
        <h1 className="font-display text-xl font-semibold">{profile.display_name ?? profile.emp_code}</h1>
        <p className="text-xs opacity-80 mt-1">
          {profile.tenant_name} · {profile.role}
        </p>
        <HomeTenantStrip />

        {checkin && (
          <Link to="/my-schedule" className="mt-3 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1 text-xs">
            {shift ? (
              <span>กะ {shift.start_time}–{shift.end_time}</span>
            ) : (
              <span>เวลาทำงานมาตรฐาน</span>
            )}
            <span className="opacity-60">·</span>
            <span className="font-medium">{nextKind ? `ถึงคิว: ${CHECKIN_LABEL[nextKind]}` : 'เช็คอินครบแล้ว'}</span>
            <ChevronRight className="w-3 h-3 opacity-70" />
          </Link>
        )}
      </header>

      <div className="px-4 pt-4 space-y-4">
        {/* setup checklist — เฉพาะแอดมินที่ยังตั้งค่าไม่ครบ (ซ่อนเองเมื่อครบ) */}
        {isAdmin && <SetupCard />}

        {/* งานด่วนวันนี้ */}
        <DailyBriefing />

        {/* action ด่วนวันนี้ — check-in เป็นปุ่มใหญ่ "คิวถัดไปตอนนี้" (Workforce: 1 จอ-1 งาน) */}
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">วันนี้</h2>
          {checkin && (
            <div className="mb-3">
              <BigActionButton
                icon={Camera}
                variant="gradient"
                label={nextKind ? `เช็คอิน · ${CHECKIN_LABEL[nextKind]}` : 'เช็คอิน'}
                sublabel={shift ? `กะ ${shift.start_time}–${shift.end_time}` : 'เวลาทำงานมาตรฐาน'}
                to="/checkin"
                done={!nextKind}
                doneLabel="เช็คอินครบแล้ววันนี้"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {(checkin ? FAST_ACTIONS.filter((a) => a.to !== '/checkin') : FAST_ACTIONS).map((item) => (
              <FastActionTile
                key={item.to}
                item={item}
                badge={item.to === '/checkin' ? checkinBadge : undefined}
              />
            ))}
          </div>
        </section>

        {/* เมนูที่เหลือ แบ่งหมวด */}
        {visibleGroups.map((g) => (
          <section key={g.title}>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{g.title}</h2>
            <div className="card divide-y divide-gray-100">
              {g.items.map((m) => {
                const Icon = m.icon;
                return (
                  <Link
                    key={m.to}
                    to={m.to}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition first:rounded-t-xl last:rounded-b-xl"
                  >
                    <Icon className="w-5 h-5 text-brand-600 shrink-0" />
                    <span className="text-sm font-medium flex-1">{m.label}</span>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <BottomTabBar />
    </div>
  );
}
